import React, { useState } from "react";
import { Card, Row, Col, Form, InputGroup, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import { COLORS, DEFAULT_NEW_PROFESSION, NewProfessionForm } from "../../types/profession.types";
import { createAdminProfession } from "../../../../services/professionService";

interface AddProfessionTabProps {
  onProfessionAdded: () => void;
}

export const AddProfessionTab: React.FC<AddProfessionTabProps> = ({ onProfessionAdded }) => {
  const [form, setForm] = useState<NewProfessionForm>(DEFAULT_NEW_PROFESSION);
  const [adding, setAdding] = useState(false);

  const styles = {
    label: { color: COLORS.text, fontWeight: 500, fontSize: "0.9rem", marginBottom: "6px" },
    input: { borderColor: COLORS.border, fontSize: "15px", height: "46px", borderRadius: "8px" },
    inputGroupText: { height: "46px", backgroundColor: COLORS.light, borderColor: COLORS.border, color: COLORS.text },
    badge: { backgroundColor: COLORS.light, color: COLORS.textLight, padding: "4px 10px", borderRadius: "20px", fontSize: "0.8rem", whiteSpace: "nowrap" as const },
    button: { height: "46px", borderRadius: "8px", fontSize: "15px", fontWeight: 500 },
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      return toast.error("Le nom de la profession est requis");
    }

    try {
      setAdding(true);
      await createAdminProfession(form);
      toast.success(`✅ "${form.name}" a été ajoutée avec succès`);
      setForm(DEFAULT_NEW_PROFESSION);
      onProfessionAdded();
    } catch (error) {
      toast.error("❌ Erreur lors de l'ajout de la profession");
      console.error("Erreur ajout profession:", error);
    } finally {
      setAdding(false);
    }
  };

  const handleCancel = () => {
    setForm(DEFAULT_NEW_PROFESSION);
    onProfessionAdded();
  };

  return (
    <Card className="border-0 shadow-sm" style={{ borderRadius: "16px", overflow: "hidden" }}>
      <Card.Body className="p-4 p-lg-5">
        {/* En-tête */}
        <div className="mb-4">
          <h3 className="h5 fw-semibold mb-1" style={{ color: COLORS.text }}>
            ➕ Ajouter une nouvelle profession
          </h3>
          <p style={{ color: COLORS.textLight, fontSize: "0.9rem", marginBottom: 0 }}>
            Configurez les paramètres de base pour une nouvelle profession
          </p>
        </div>

        {/* Formulaire */}
        <Row className="g-4">
          <Col xs={12}>
            <Form.Label style={styles.label}>
              <i className="bi bi-briefcase me-2" style={{ color: COLORS.primary }}></i>
              Nom de la profession <span style={{ color: COLORS.danger }}>*</span>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="ex: Architecte, Médecin, Avocat..."
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              disabled={adding}
              style={styles.input}
            />
            {!form.name.trim() && (
              <Form.Text className="text-muted mt-1">Ce champ est obligatoire</Form.Text>
            )}
          </Col>

          <Col xs={12} md={6}>
            <Form.Label style={styles.label}>
              <i className="bi bi-crown me-2" style={{ color: COLORS.secondary }}></i>
              Places Premium
            </Form.Label>
            <Form.Control
              type="number"
              min="1"
              max="50"
              placeholder="10"
              value={form.max_premium_slots ?? ""}
              onChange={(e) => setForm({ ...form, max_premium_slots: e.target.value ? Number(e.target.value) : 10 })}
              disabled={adding}
              style={styles.input}
              inputMode="numeric"
            />
          </Col>

          <Col xs={12} md={6}>
            <Form.Label style={styles.label}>
              <i className="bi bi-coin me-2" style={{ color: COLORS.secondary }}></i>
              Prix Premium
            </Form.Label>
            <InputGroup>
              <Form.Control
                type="number"
                min="0"
                step="10"
                placeholder="0"
                value={form.premium_price ?? ""}
                onChange={(e) => setForm({ ...form, premium_price: e.target.value ? Number(e.target.value) : null })}
                disabled={adding}
                style={styles.input}
                inputMode="numeric"
              />
              <InputGroup.Text style={styles.inputGroupText}>€</InputGroup.Text>
            </InputGroup>
          </Col>

          <Col xs={12} md={6}>
            <Form.Label style={styles.label}>
              <i className="bi bi-file-spreadsheet me-2" style={{ color: COLORS.primary }}></i>
              Prix Export
            </Form.Label>
            <InputGroup>
              <Form.Control
                type="number"
                min="0"
                step="5"
                placeholder="0 = gratuit"
                value={form.export_price ?? ""}
                onChange={(e) => setForm({ ...form, export_price: e.target.value ? Number(e.target.value) : null })}
                disabled={adding}
                style={styles.input}
                inputMode="numeric"
              />
              <InputGroup.Text style={styles.inputGroupText}>€</InputGroup.Text>
            </InputGroup>
          </Col>

          <Col xs={12} md={6}>
            <div style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
              <Form.Label style={{ ...styles.label, opacity: 0.7 }}>
                <i className="bi bi-star me-2" style={{ color: COLORS.secondary }}></i>
                Priorité
              </Form.Label>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", backgroundColor: COLORS.light, padding: "8px 16px", borderRadius: "8px", height: "46px" }}>
                <div className="d-flex align-items-center gap-2">
                  <span style={{ color: COLORS.text, fontSize: "0.95rem" }}>Profession prioritaire</span>
                  <span style={styles.badge}>⭐ Visible en haut</span>
                </div>
                <Form.Check
                  type="switch"
                  id="priority"
                  checked={form.is_priority}
                  onChange={(e) => setForm({ ...form, is_priority: e.target.checked })}
                  disabled={adding}
                  style={{ marginLeft: "12px" }}
                />
              </div>
            </div>
          </Col>
        </Row>

        <hr className="my-4" style={{ borderColor: COLORS.border }} />
        
        {/* Boutons d'action */}
        <div className="d-flex flex-column flex-sm-row justify-content-end gap-3">
          <Button
            onClick={handleCancel}
            variant="outline-secondary"
            disabled={adding}
            style={{ ...styles.button, minWidth: "140px", borderColor: COLORS.border, color: COLORS.textLight }}
            className="w-100 w-sm-auto"
          >
            <i className="bi bi-x-circle me-2"></i> Annuler
          </Button>

          <Button
            onClick={handleSubmit}
            disabled={adding || !form.name.trim()}
            style={{ ...styles.button, minWidth: "200px", backgroundColor: COLORS.primary, border: "none", color: "white", opacity: adding || !form.name.trim() ? 0.7 : 1 }}
            className="w-100 w-sm-auto"
          >
            {adding ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Création en cours...
              </>
            ) : (
              <>
                <i className="bi bi-check-circle me-2"></i> Créer la profession
              </>
            )}
          </Button>
        </div>

        {/* Information supplémentaire */}
        <div className="mt-4 p-3 rounded" style={{ backgroundColor: COLORS.light, border: `1px solid ${COLORS.border}`, fontSize: "0.85rem", color: COLORS.textLight }}>
          <i className="bi bi-info-circle me-2" style={{ color: COLORS.primary }}></i>
          Les professions créées ici apparaîtront dans l'interface de tarification et pourront être sélectionnées par les utilisateurs lors de leur inscription.
        </div>
      </Card.Body>
    </Card>
  );
};